import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Icon, Modal, Tooltip, message } from 'antd'

import LangContext from 'modules/shared/context/langContext'
import actions from '../actions'
import { isVoiceEnabled } from '../lib/config'
import {
  callDisabledReason,
  isCallableVehicle,
  isVoiceUsable,
  presenceVehicleById,
} from '../lib/helpers'
import { DISABLED_REASON_KEYS, voiceText } from './labels'

// The phone icon of a vehicle table row (e.g. the honeytoast live stream list)
// as the call control. It finds the vehicle in gateway presence by vehicleId
// (CPAC vehicles.id), and the icon's colour says whether it can be called now:
//   green  - callable. A click, Enter or Space opens a Modal.confirm; OK
//            re-checks the latest state before dialling.
//   orange - this browser or the vehicle is already on a call.
//   grey   - SIP is not registered yet, or the vehicle is not connected to
//            calling (no online presence entry with a dialable SIP user:
//            sip_user, or the IMEI from an older gateway; see dialTarget).
// The tooltip gives the reason. A click on the call control or its tooltip
// never reaches the row, whose cell click toggles the vehicle's video; a
// click on a not-connected icon still does, as it did before calling existed.
//
// When calling is off or the account may not use voice, the icon renders
// exactly as the table drew it, without a tooltip.
//
// Tables can hold thousands of rows, each connected to the store, so a row
// receives only its derived status and presence entry: a SIP or call change
// re-renders the callable rows, not all of them, and a presence update only
// the rows whose entry changed. The not-connected icon uses a native title
// rather than an antd Tooltip for the same reason.
//
// Props:
//   vehicleId - CPAC vehicles.id of the row
//   plateNo   - plate named in the confirmation
//   phoneText - the phone text the table shows, repeated in the confirmation
//   iconStyle - style the table gives the icon
class VehiclePhoneCall extends Component {
  static contextType = LangContext

  // { destroy, update } of the open confirmation; at most one is open.
  confirmDialog = null

  componentWillUnmount() {
    if (this.confirmDialog) this.confirmDialog.destroy()
    this.confirmDialog = null
  }

  // Why the vehicle cannot be called with the current props (null = it can).
  blockedReason() {
    const { status } = this.props
    if (status === READY) return null
    if (status === PLAIN) return 'notRegistered'
    if (status === NOT_CONNECTED) return 'vehicleOffline'
    return status
  }

  openConfirm() {
    if (this.confirmDialog || this.blockedReason()) return
    // Modal.confirm renders outside this tree, where LangContext only has its
    // default value, so every text is resolved here.
    const i18n = this.context
    const t = key => voiceText(i18n, key)
    const { vehicle, plateNo, phoneText } = this.props
    const plate = plateNo || vehicle.plate || vehicle.code || '-'

    this.confirmDialog = Modal.confirm({
      title: t('voiceConfirmCall'),
      content: (
        <div>
          <div>{t('voiceConfirmCallVehicle').replace('{plate}', plate)}</div>
          {!isBlank(phoneText) && (
            <div style={{ marginTop: 8 }}>
              <Icon type="phone" style={{ transform: 'scaleX(-1)' }} />{' '}
              {phoneText}
            </div>
          )}
        </div>
      ),
      okText: t('voiceCall'),
      cancelText: i18n && i18n.c ? i18n.c.cancel : undefined,
      // antd focuses OK by default, where the auto-repeat of the Enter that
      // opened the dialog would confirm it.
      autoFocusButton: 'cancel',
      onOk: this.handleConfirm,
      onCancel: this.handleCancel,
    })
  }

  // The dialog may have been open for a while, so this decides on the latest
  // props. Only the first OK of a dialog counts.
  handleConfirm = () => {
    if (!this.confirmDialog) return
    this.confirmDialog = null
    const reason = this.blockedReason()
    if (reason) {
      message.warning(voiceText(this.context, DISABLED_REASON_KEYS[reason]))
      return
    }
    this.props.makeCall(this.props.vehicle)
  }

  handleCancel = () => {
    this.confirmDialog = null
  }

  handleClick = e => {
    e.stopPropagation()
    this.openConfirm()
  }

  // Like a native button: Enter acts on keydown, Space on keyup, so the key
  // release cannot land on a button of the dialog. A held Enter's auto-repeat
  // is ignored: after Cancel the focus is back here, and it would reopen.
  handleKeyDown = e => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    e.stopPropagation()
    if (e.key === 'Enter' && !e.repeat) this.openConfirm()
  }

  handleKeyUp = e => {
    if (e.key !== ' ') return
    e.preventDefault()
    e.stopPropagation()
    this.openConfirm()
  }

  render() {
    const { status, plateNo, iconStyle } = this.props
    if (status === PLAIN) return <Icon type="phone" style={iconStyle} />

    const t = key => voiceText(this.context, key)
    if (status === NOT_CONNECTED) {
      return (
        <Icon
          type="phone"
          title={t(DISABLED_REASON_KEYS.vehicleOffline)}
          style={{ ...iconStyle, color: GREY }}
        />
      )
    }

    const reason = this.blockedReason()
    const label = reason
      ? t(DISABLED_REASON_KEYS[reason])
      : t('voiceCallVehicle')

    // The tooltip renders in a portal whose clicks still bubble up the React
    // tree to the row, so the outer span stops them.
    return (
      <span onClick={stopPropagation}>
        <Tooltip title={label}>
          <span
            role="button"
            tabIndex={0}
            aria-label={plateNo ? `${label} ${plateNo}` : label}
            aria-disabled={reason ? true : undefined}
            aria-haspopup={reason ? undefined : 'dialog'}
            onClick={this.handleClick}
            onKeyDown={this.handleKeyDown}
            onKeyUp={this.handleKeyUp}
            style={{ cursor: reason ? 'not-allowed' : 'pointer' }}
          >
            <Icon
              type="phone"
              theme={reason ? 'outlined' : 'filled'}
              style={{
                ...iconStyle,
                color: reason ? REASON_COLORS[reason] : GREEN,
              }}
            />
          </span>
        </Tooltip>
      </span>
    )
  }
}

function isBlank(text) {
  if (typeof text === 'string') return text.trim() === ''
  return text === undefined || text === null || text === false
}

const stopPropagation = e => e.stopPropagation()

// Whether two presence entries carry the same fields.
function sameEntry(a, b) {
  if (a === b) return true
  if (!a || !b) return false
  const keys = Object.keys(a)
  return (
    keys.length === Object.keys(b).length &&
    keys.every(
      key => Object.prototype.hasOwnProperty.call(b, key) && a[key] === b[key],
    )
  )
}

// Row statuses besides the callDisabledReason codes of a callable vehicle.
const PLAIN = 'plain'
const NOT_CONNECTED = 'notConnected'
const READY = 'ready'

const GREEN = '#52c41a'
const ORANGE = '#faad14'
const GREY = '#bfbfbf'

const REASON_COLORS = {
  notRegistered: GREY,
  anotherCall: ORANGE,
  inCall: GREEN,
  vehicleBusy: ORANGE,
  vehicleOffline: GREY,
}

// Constant results, so connect sees no change for rows that cannot be called.
const PLAIN_PROPS = { status: PLAIN, vehicle: null }
const NOT_CONNECTED_PROPS = { status: NOT_CONNECTED, vehicle: null }

const mapStateToProps = ({ auth, voice }, { vehicleId }) => {
  if (!isVoiceEnabled() || !isVoiceUsable(auth.profile, voice)) {
    return PLAIN_PROPS
  }
  const vehicle = presenceVehicleById(voice.vehicles, vehicleId)
  if (!isCallableVehicle(vehicle)) return NOT_CONNECTED_PROPS
  const reason = callDisabledReason({
    sipState: voice.sipState,
    activeCall: voice.activeCall,
    vehicle,
  })
  return { status: reason || READY, vehicle }
}

// presence_update brings every entry as a new object, on any registration or
// call in the fleet, so an entry with the same fields is no change.
const areStatePropsEqual = (next, prev) =>
  next.status === prev.status && sameEntry(next.vehicle, prev.vehicle)

const mapDispatchToProps = {
  makeCall: actions.makeCall,
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  areStatePropsEqual,
})(VehiclePhoneCall)
